import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import PlantaCleanEditor from '../components/PlantaCleanEditor';

/**
 * QCO Plan — fullscreen floor plan editor.
 * Header stays at top; editor fills the rest of the viewport.
 *
 * Data comes from localStorage ('orcai_analise_planta'), same as OrcamentoConfirmar.
 * On confirm, updates localStorage and navigates to /orcamento/confirmar.
 */
export default function QcoPlan() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tipoFluxo = searchParams.get('tipo') || 'cub';

  const [comodos, setComodos] = useState([]);
  const [imagemPlanta, setImagemPlanta] = useState(null);
  const [nomeProjeto, setNomeProjeto] = useState('');
  const [loaded, setLoaded] = useState(false);

  // Load analysis data from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('orcai_analise_planta');
      if (!stored) {
        navigate('/orcamento/novo');
        return;
      }
      const data = JSON.parse(stored);
      let comodosFinal = data.comodos || [];

      // ── SOLVER LAYOUT → BBOX INJECTION ──
      // Se o Layout Solver rodou, converter coordenadas em metros para bbox normalizado (0-1).
      // Isso alimenta o PlantaCleanEditor pelo path de bbox existente, sem mudanças no editor.
      const layout = data.layout;
      if (layout && layout.rooms && layout.rooms.length > 0 && layout.envelope_m) {
        const envW = layout.envelope_m.w;
        const envH = layout.envelope_m.h;
        console.log(`[QCO-PLAN] Solver layout detectado: ${layout.rooms.length} rooms, envelope ${envW}x${envH}m`);

        comodosFinal = comodosFinal.map((comodo) => {
          // Match solver room pelo nome (case-insensitive, trim)
          const nomeNorm = (comodo.nome || '').trim().toLowerCase();
          const solverRoom = layout.rooms.find(
            (r) => (r.nome || '').trim().toLowerCase() === nomeNorm
          );

          if (!solverRoom) {
            console.log(`[QCO-PLAN] Sem match solver para "${comodo.nome}"`);
            return comodo;
          }

          // Converter coordenadas do solver (metros) para 0-1 normalizado
          // TODOS os rooms usam geometry.vertices (mesmo rects) para garantir
          // que passam pelo pipeline de geometria do editor (useGridSnap → polygon)
          // e NÃO dependem de alinhamento com a imagem de fundo.
          let vertices;

          if (solverRoom.tipo === 'polygon' && solverRoom.vertices_m) {
            vertices = solverRoom.vertices_m.map(([mx, my]) => [mx / envW, my / envH]);
          } else if (solverRoom.tipo === 'rect' && solverRoom.position_m) {
            const p = solverRoom.position_m;
            // Rect → 4 vértices (sentido horário)
            vertices = [
              [p.x / envW, p.y / envH],                      // topo-esq
              [(p.x + p.w) / envW, p.y / envH],              // topo-dir
              [(p.x + p.w) / envW, (p.y + p.h) / envH],      // base-dir
              [p.x / envW, (p.y + p.h) / envH],              // base-esq
            ];
          }

          if (!vertices || vertices.length < 3) return comodo;

          // Bounding box para fallback
          const xs = vertices.map((v) => v[0]);
          const ys = vertices.map((v) => v[1]);
          const minX = Math.min(...xs);
          const minY = Math.min(...ys);

          console.log(`[QCO-PLAN] ${comodo.nome}: ${vertices.length} verts, tipo=${solverRoom.tipo}`);

          return {
            ...comodo,
            bbox: { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY },
            geometry: {
              vertices,
              tipo: solverRoom.tipo || 'rect',
            },
            _solver: true,
            _solverWalls: solverRoom.walls || [],
          };
        });

        console.log(`[QCO-PLAN] ${comodosFinal.filter((c) => c._solver).length}/${comodosFinal.length} cômodos com posição do Solver`);
      }

      setComodos(comodosFinal);
      setImagemPlanta(data.imagem_planta || null);
      setNomeProjeto(data.config?.nomeProjeto || data.nome_projeto || 'Projeto');
      setLoaded(true);
    } catch {
      navigate('/orcamento/novo');
    }
  }, [navigate]);

  // When user confirms from the editor, update localStorage and go to OrcamentoConfirmar
  const handleConfirm = useCallback((updatedRooms) => {
    try {
      const stored = localStorage.getItem('orcai_analise_planta');
      if (stored) {
        const data = JSON.parse(stored);

        // Merge updated rooms back into the analysis data
        data.comodos = (data.comodos || []).map((c, i) => {
          if (!updatedRooms[i]) return c;
          const u = updatedRooms[i];
          return {
            ...c,
            nome: u.nome || c.nome,
            bbox: u.bbox || c.bbox,
            acabamento: u.acabamento || c.acabamento,
            esquadrias: u.esquadrias || c.esquadrias,
          };
        });

        localStorage.setItem('orcai_analise_planta', JSON.stringify(data));
      }
    } catch (err) {
      console.error('QcoPlan: erro ao atualizar localStorage', err);
    }

    // Navigate to OrcamentoConfirmar to continue the budget flow
    navigate(`/orcamento/confirmar?tipo=${tipoFluxo}`);
  }, [navigate, tipoFluxo]);

  if (!loaded) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header />
        <main className="flex-1 flex items-center justify-center pt-16">
          <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      {/* Editor fills entire viewport below header (h-16 = 64px) */}
      <main
        style={{
          paddingTop: 64,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ flex: 1, minHeight: 0 }}>
          <PlantaCleanEditor
            comodos={comodos}
            imagemPlanta={imagemPlanta}
            projectName={nomeProjeto}
            onConfirm={handleConfirm}
            onRoomsChange={() => {}}
            fullscreen
          />
        </div>
      </main>
    </div>
  );
}
